/**
 * Upload to FusionDocs: pick or drop files, fill a short form per file, then each goes up to ONE (in pieces when
 * big, via TaskFiles' upload helper) and is filed into FusionDocs. Shows progress and opens the stored result.
 */
import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { CheckCircle2, Loader2, Lock, Trash2, Upload as UploadIcon } from "lucide-react";
import { useRef, useState } from "react";
import { api } from "../../lib/api";
import { upload } from "../TaskFiles";
import { Modal } from "../ui";
import { FolderPicker } from "./FolderPicker";
import { cleanPath, errText, FD_MAX, humanSize, type FdFileResult } from "./fd";

type Conf = "internal" | "confidential" | "restricted";
interface Entry {
  key: string; file: File; title: string; type: string; company: string; confidentiality: Conf; onlyMe: boolean; folder: string;
  state: "draft" | "uploading" | "filing" | "done" | "error"; p: number; error?: string; result?: FdFileResult;
}

const stripExt = (n: string) => n.replace(/\.[A-Za-z0-9]{1,6}$/, "") || n;

export function UploadDialog({ onClose, onOpen }: { onClose: () => void; onOpen: (id: string) => void }) {
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [entries, setEntries] = useState<Entry[]>([]);
  const [drag, setDrag] = useState(false);
  const [running, setRunning] = useState(false);

  const add = (list: File[]) => setEntries((cur) => [...cur, ...list.map((file): Entry => ({
    key: crypto.randomUUID(), file, title: stripExt(file.name), type: "", company: "", confidentiality: "internal", onlyMe: false, folder: "",
    state: file.size > FD_MAX ? "error" : "draft", p: 0, error: file.size > FD_MAX ? "FusionDocs takes files up to 99 MB" : undefined,
  }))]);
  const set = (key: string, patch: Partial<Entry>) => setEntries((cur) => cur.map((e) => (e.key === key ? { ...e, ...patch } : e)));
  const ready = entries.filter((e) => e.state === "draft" && e.title.trim());

  const send = async () => {
    setRunning(true);
    await Promise.all(ready.map(async (e) => {
      set(e.key, { state: "uploading", p: 0, error: undefined });
      try {
        const { id } = await upload(e.file, (p) => set(e.key, { p }));
        set(e.key, { state: "filing", p: 1 });
        const r = await api<FdFileResult>("/api/fd/file", { method: "POST", json: {
          documentId: id, title: e.title.trim(), ...(e.type.trim() ? { type: e.type.trim() } : {}), ...(e.company.trim() ? { company: e.company.trim() } : {}),
          confidentiality: e.confidentiality, onlyMe: e.onlyMe, ...(cleanPath(e.folder) ? { folder: cleanPath(e.folder) } : {}),
        } });
        const bad = ["rejected", "quarantined", "scan_failed"].includes(r.status);
        set(e.key, bad
          ? { state: "error", error: r.status === "quarantined" ? "The virus scan stopped this file" : r.status === "scan_failed" ? "The virus scan failed; try again" : "FusionDocs refused this file" }
          : { state: "done", result: r });
      } catch (err) {
        set(e.key, { state: "error", error: errText(err) });
      }
    }));
    setRunning(false);
    void qc.invalidateQueries({ queryKey: ["fd"] });
  };

  const drafts = entries.filter((e) => e.state === "draft").length;
  return (
    <Modal title="Upload to Documents" wide onClose={() => !running && onClose()}
      footer={<>
        <button className="btn" onClick={onClose} disabled={running}>{entries.some((e) => e.state === "done") ? "Done" : "Cancel"}</button>
        <button className="btn btn-primary" disabled={running || ready.length === 0} onClick={() => void send()}>
          {running ? <><Loader2 size={14} className="animate-spin" /> Uploading…</> : <><UploadIcon size={14} /> {ready.length > 1 ? `Upload ${ready.length} files` : "Upload"}</>}
        </button>
      </>}>
      <button
        type="button"
        onClick={() => input.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); add(Array.from(e.dataTransfer.files)); }}
        className={clsx("muted flex w-full flex-col items-center gap-1 rounded-xl border-2 border-dashed px-4 py-6 text-sm transition-colors hover:border-brand-300 hover:text-brand-700",
          drag ? "border-brand-400 bg-brand-50 text-brand-700" : "border-[var(--line)]")}
      >
        <UploadIcon size={18} aria-hidden />
        <span>Drop files here, or <span className="font-semibold text-brand-600">choose files</span></span>
        <span className="text-xs">Up to 99 MB each</span>
      </button>
      <input ref={input} type="file" multiple hidden onChange={(e) => { add(Array.from(e.target.files ?? [])); e.target.value = ""; }} />

      {entries.length > 0 && (
        <ul className="space-y-3">
          {entries.map((e) => (
            <li key={e.key} className="rounded-xl border border-[var(--line)] p-3">
              <div className="flex items-center gap-2 text-sm">
                <span className="min-w-0 flex-1 truncate font-medium" title={e.file.name}>{e.file.name}</span>
                <span className="muted shrink-0 text-xs">{humanSize(e.file.size)}</span>
                {(e.state === "draft" || e.state === "error") && !running && (
                  <button className="btn btn-ghost btn-sm !px-1.5" onClick={() => setEntries((cur) => cur.filter((x) => x.key !== e.key))} aria-label={`Remove ${e.file.name}`}><Trash2 size={14} /></button>
                )}
              </div>

              {e.state === "draft" && (
                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  <label className="block text-xs sm:col-span-2">
                    <span className="mb-1 block font-semibold text-[var(--text-2)]">Title</span>
                    <input className="input w-full" value={e.title} maxLength={300} onChange={(x) => set(e.key, { title: x.target.value })} />
                  </label>
                  <label className="block text-xs">
                    <span className="mb-1 block font-semibold text-[var(--text-2)]">Type (optional)</span>
                    <input className="input w-full" value={e.type} maxLength={60} placeholder="e.g. Contract, Invoice" onChange={(x) => set(e.key, { type: x.target.value })} />
                  </label>
                  <label className="block text-xs">
                    <span className="mb-1 block font-semibold text-[var(--text-2)]">Company (optional)</span>
                    <input className="input w-full" value={e.company} maxLength={80} placeholder="The company’s full name, as in FusionDocs" onChange={(x) => set(e.key, { company: x.target.value })} />
                  </label>
                  <label className="block text-xs">
                    <span className="mb-1 block font-semibold text-[var(--text-2)]">Confidentiality</span>
                    <select className="input w-full" value={e.confidentiality} onChange={(x) => set(e.key, { confidentiality: x.target.value as Conf })}>
                      <option value="internal">Internal</option>
                      <option value="confidential">Confidential</option>
                      <option value="restricted">Restricted</option>
                    </select>
                  </label>
                  <label className="flex items-center gap-2 self-end pb-2 text-sm">
                    <input type="checkbox" checked={e.onlyMe} onChange={(x) => set(e.key, { onlyMe: x.target.checked })} />
                    <Lock size={13} className="muted" aria-hidden /> Only me (private)
                  </label>
                  <div className="text-xs sm:col-span-2">
                    <span className="mb-1 block font-semibold text-[var(--text-2)]">Folder (optional)</span>
                    <FolderPicker value={e.folder} onChange={(v) => set(e.key, { folder: v })} collapsed />
                    <span className="muted mt-1 block">Leave empty and FusionDocs files it for you.</span>
                  </div>
                </div>
              )}

              {(e.state === "uploading" || e.state === "filing") && (
                <div className="mt-2 text-xs" role="status">
                  <span className="muted flex justify-between gap-2">
                    <span>{e.state === "filing" ? "Filing in FusionDocs (virus scan)…" : "Uploading…"}</span>
                    <span className="num">{Math.round(e.p * 100)}%</span>
                  </span>
                  <span className="mt-1 block h-1.5 overflow-hidden rounded bg-black/[0.06]">
                    <span className={clsx("block h-full bg-brand-500 transition-all", e.state === "filing" && "animate-pulse")} style={{ width: `${e.p * 100}%` }} />
                  </span>
                </div>
              )}

              {e.state === "done" && e.result && (
                <div className="mt-2 flex flex-wrap items-center gap-2 text-xs" role="status">
                  <CheckCircle2 size={14} className="text-emerald-600" aria-hidden />
                  <span className="flex-1">{e.result.status === "stored" ? "Stored in FusionDocs" : "Received; FusionDocs is still scanning it"}{e.result.companyNotFound && <span className="block text-xs text-amber-800">FusionDocs doesn’t know the company “{e.result.companyNotFound}”, so it was filed without one. Use the company’s full name to tag it.</span>}</span>
                  {e.result.documentId && (
                    <button className="btn btn-sm" onClick={() => { onOpen(e.result!.documentId!); onClose(); }}>Open</button>
                  )}
                </div>
              )}

              {e.state === "error" && <p role="alert" className="mt-2 text-xs text-red-700">{e.error}</p>}
            </li>
          ))}
        </ul>
      )}
      {drafts > 0 && ready.length < drafts && <p className="muted text-xs">Every file needs a title.</p>}
    </Modal>
  );
}
